import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AppLayout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import {
  Compass, Calculator, Map, GraduationCap, Building, Wallet,
  CheckCircle2, AlertCircle, ArrowRight, Sparkles,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StartHerePage() {
  const { user, getAuthHeader } = useAuth();
  const [cdp, setCdp] = useState(null);
  const [cost, setCost] = useState(null);
  const [colleges, setColleges] = useState(0);

  useEffect(() => {
    axios.get(`${API}/nc/cdp/status`, getAuthHeader()).then(r => setCdp(r.data)).catch(() => {});
    axios.get(`${API}/cost-model/summary`, getAuthHeader()).then(r => setCost(r.data)).catch(() => {});
    axios.get(`${API}/colleges/favorites`, getAuthHeader())
      .then(r => setColleges((r.data?.colleges || r.data || []).length)).catch(() => {});
  }, [getAuthHeader]);

  const firstName = user?.first_name || (user?.full_name || '').split(' ')[0] || 'there';

  const steps = [
    { id: 'assessment', label: 'Assessment', to: '/app/assessment', icon: Compass,
      blurb: 'Discover your interests, personality, work environment and values.',
      done: !!cdp?.has_assessment },
    { id: 'design', label: 'Design My Life', to: '/app/calculator', icon: Calculator,
      blurb: 'Price out the life you want so every path can be judged against a real number.',
      done: !!cost?.started },
    { id: 'path', label: 'Choose My Path', to: '/app/pathways', icon: Map,
      blurb: 'College, trades, military, entrepreneurship or workforce. Pick your direction.',
      done: !!cdp?.has_career_goal },
    { id: 'academic', label: 'Academic Profile', to: '/app/course-planner', icon: GraduationCap,
      blurb: 'Grades, courses and credits. What you have already earned.',
      done: !!cdp?.has_course_plan },
    { id: 'colleges', label: 'Education Preferences', to: '/app/college-intel', icon: Building,
      blurb: 'Size, setting, distance and cost ceiling so recommendations fit you.',
      done: colleges > 0 },
    { id: 'financial', label: 'Financial Profile', to: '/app/financial', icon: Wallet,
      blurb: 'What your family can contribute and what you need to cover.',
      done: !!cost?.started },
  ];

  const doneCount = steps.filter(s => s.done).length;
  const pct = Math.round((doneCount / steps.length) * 100);
  const incomplete = steps.filter(s => !s.done);

  return (
    <AppLayout>
      <div className="max-w-[1200px] mx-auto space-y-5 pb-16 px-3 sm:px-6" data-testid="start-here-page">

        {/* ── Compact Hero ──────────────────────────────────────────── */}
        <div className="relative overflow-hidden rounded-[16px] px-5 py-5 sm:px-7 sm:py-6"
          style={{ background: 'rgba(6,23,54,0.72)', backdropFilter: 'blur(16px)', border: '1px solid rgba(57,227,220,0.28)',
            boxShadow: '0 0 6px rgba(57,227,220,0.5), 0 0 16px rgba(57,227,220,0.2)' }}>
          <div aria-hidden className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full blur-3xl opacity-25"
            style={{ background: 'radial-gradient(circle,#39E3DC,transparent 65%)' }} />
          <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px"
            style={{ background: 'linear-gradient(90deg, transparent, rgba(57,227,220,0.5), transparent)' }} />

          <div className="relative flex items-center justify-between gap-6 flex-wrap">
            <div className="min-w-0 flex-1">
              <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-white tracking-tight"
                style={{ textShadow: '0 0 40px rgba(57,227,220,0.15)' }}>
                {firstName}, let's build your profile.
              </h1>
              <p className="mt-1.5 text-sm text-slate-300/80 max-w-xl">
                Complete these steps and everything after, your plan, careers, colleges and finances, becomes personalised to you.
              </p>
            </div>

            {/* Ring */}
            <div className="relative shrink-0">
              <svg width="100" height="100" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="40" fill="none" stroke="rgba(57,227,220,0.08)" strokeWidth="8" />
                <motion.circle cx="50" cy="50" r="40" fill="none" stroke="url(#pGrad)" strokeWidth="8"
                  strokeLinecap="round" transform="rotate(-90 50 50)"
                  strokeDasharray={2 * Math.PI * 40}
                  initial={{ strokeDashoffset: 2 * Math.PI * 40 }}
                  animate={{ strokeDashoffset: 2 * Math.PI * 40 * (1 - pct / 100) }}
                  transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                  style={{ filter: 'drop-shadow(0 0 6px rgba(57,227,220,0.5))' }} />
                <defs>
                  <linearGradient id="pGrad" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#39E3DC" />
                    <stop offset="100%" stopColor="#782FE1" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="font-outfit text-2xl font-extrabold text-white tabular-nums" data-testid="start-here-completion">{pct}%</span>
                <span className="text-[8px] uppercase tracking-[0.15em] text-[#39E3DC] font-bold">Complete</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Step cards ────────────────────────────────────────────── */}
        <div className="grid gap-3 md:grid-cols-2">
          {steps.map((s, i) => (
            <motion.div key={s.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.03 * i }}>
              <Link to={s.to} data-testid={`start-here-step-${s.id}`}
                className={`group flex h-full gap-4 rounded-[14px] border p-4 transition-all duration-300 hover:scale-[1.01] hover:-translate-y-0.5 relative overflow-hidden`}
                style={{
                  background: s.done ? 'rgba(6,23,54,0.72)' : 'rgba(6,23,54,0.85)',
                  borderColor: s.done ? 'rgba(21,186,137,0.3)' : 'rgba(244,63,94,0.3)',
                  boxShadow: s.done
                    ? '0 0 1px rgba(21,186,137,0.4), 0 0 12px -4px rgba(21,186,137,0.15)'
                    : '0 0 1px rgba(244,63,94,0.4), 0 0 16px -4px rgba(244,63,94,0.12)',
                }}>
                {/* Top glow */}
                <div className="absolute inset-x-0 top-0 h-px pointer-events-none"
                  style={{ background: s.done
                    ? 'linear-gradient(90deg, transparent, rgba(21,186,137,0.4), transparent)'
                    : 'linear-gradient(90deg, transparent, rgba(244,63,94,0.4), transparent)' }} />

                {/* Status */}
                <div className="shrink-0 mt-0.5">
                  {s.done ? (
                    <div className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ background: 'rgba(21,186,137,0.12)', boxShadow: '0 0 10px rgba(21,186,137,0.2)' }}>
                      <CheckCircle2 className="h-5 w-5 text-[#15BA89]" style={{ filter: 'drop-shadow(0 0 4px rgba(21,186,137,0.5))' }} />
                    </div>
                  ) : (
                    <motion.div
                      animate={{ boxShadow: ['0 0 6px rgba(244,63,94,0.2)', '0 0 18px rgba(244,63,94,0.5)', '0 0 6px rgba(244,63,94,0.2)'] }}
                      transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                      className="w-8 h-8 rounded-full flex items-center justify-center border-2 border-dashed border-[#f43f5e]/50"
                      style={{ background: 'rgba(244,63,94,0.06)' }}>
                      <AlertCircle className="h-4 w-4 text-[#f43f5e]" />
                    </motion.div>
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <s.icon className="h-4 w-4 text-[#39E3DC]" style={{ filter: 'drop-shadow(0 0 3px rgba(57,227,220,0.4))' }} />
                    <p className="text-sm font-bold text-white">{s.label}</p>
                    {s.done ? (
                      <span className="text-[9px] font-bold uppercase px-2 py-0.5 rounded-full text-[#15BA89]"
                        style={{ background: 'rgba(21,186,137,0.1)', border: '1px solid rgba(21,186,137,0.25)' }}>Complete</span>
                    ) : (
                      <motion.span
                        animate={{ opacity: [1, 0.6, 1] }}
                        transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                        className="text-[9px] font-bold uppercase px-2 py-0.5 rounded-full text-[#f43f5e]"
                        style={{ background: 'rgba(244,63,94,0.08)', border: '1px solid rgba(244,63,94,0.25)' }}>
                        Action Needed
                      </motion.span>
                    )}
                  </div>
                  <p className="mt-1 text-[12px] leading-relaxed text-[#C6D4E8]">{s.blurb}</p>
                  <span className={`mt-2 inline-flex items-center gap-1 text-[11px] font-bold transition-colors ${s.done ? 'text-[#15BA89]' : 'text-[#f43f5e]'}`}>
                    {s.done ? 'Review' : `${firstName}, start now`} <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* ── Summary ──────────────────────────────────────────────── */}
        <div className="rounded-[14px] p-4 flex items-center gap-3"
          style={{
            background: doneCount === steps.length ? 'rgba(21,186,137,0.06)' : 'rgba(6,23,54,0.72)',
            border: `1px solid ${doneCount === steps.length ? 'rgba(21,186,137,0.3)' : 'rgba(57,227,220,0.15)'}`,
          }}>
          {doneCount === steps.length && <Sparkles className="w-5 h-5 text-[#15BA89] flex-shrink-0" style={{ filter: 'drop-shadow(0 0 4px rgba(21,186,137,0.5))' }} />}
          <p className="text-sm text-slate-300">
            <strong className="text-white">{doneCount} of {steps.length} complete.</strong>{' '}
            {doneCount === steps.length
              ? `${firstName}, your profile is working for you. Every recommendation is based on it.`
              : `Each step you finish sharpens your plan and recommendations, ${firstName}.`}
          </p>
        </div>
      </div>
    </AppLayout>
  );
}
